import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import settingsService, { AppConfig, SiteConfig } from '../services/settings.service';
import { getAuthToken } from '../utils/auth';
import { setSentryTenant } from '../lib/sentry';

interface BusinessSettingsContextType {
  // Settings data
  settings: AppConfig | null;
  siteConfig: SiteConfig | null;
  isLoading: boolean;
  isError: boolean;

  // Actions
  fetchSettings: () => Promise<void>;
  updateSettings: (data: AppConfig) => Promise<boolean>;
  updateSiteConfig: (data: Partial<SiteConfig>) => Promise<boolean>;

  // Logo URLs for easy access
  expandedLogoUrl: string | null;
  collapsedLogoUrl: string | null;
}

const defaultContext: BusinessSettingsContextType = {
  settings: null,
  siteConfig: null,
  isLoading: true,
  isError: false,
  fetchSettings: async () => {},
  updateSettings: async () => false,
  updateSiteConfig: async () => false,
  expandedLogoUrl: null,
  collapsedLogoUrl: null,
};

const BusinessSettingsContext = createContext<BusinessSettingsContextType>(defaultContext);

export const useBusinessSettings = () => {
  const context = useContext(BusinessSettingsContext);
  if (!context) {
    throw new Error('useBusinessSettings must be used within a BusinessSettingsProvider');
  }
  return context;
};

interface BusinessSettingsProviderProps {
  children: React.ReactNode;
}

export const BusinessSettingsProvider: React.FC<BusinessSettingsProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<AppConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const requireToken = (): string | null => {
  const token = getAuthToken();
  if (!token) {
    return null; 
  }
  return token;
};


  const fetchSettings = useCallback(async () => {
    const token = requireToken();
    if (!token) {
      console.log('⚠️ No auth token found, skipping business settings fetch');
      setIsLoading(false);
      return;
    }

    try {
      console.log('🔄 Fetching business settings with token...');
      setIsLoading(true);
      setIsError(false);
      const response = await settingsService.getSettings();

      if (response.success && response.data) {
        setSettings(response.data);
        console.log('✅ Business settings loaded successfully:', response.data);

        // Tag Sentry events with tenant/site name
        if (response.data.site?.site_name) {
          setSentryTenant(response.data.site.site_name);
        }
      } else {
        setSettings(null);
        console.warn('⚠️ Business settings response was not successful');
      }
    } catch (error: any) {
      console.error('❌ Error fetching business settings:', error);
      setIsError(true);
      setSettings(null);

      // Only show error toast if it's not a 404 (which means no settings exist yet)
      if (error.response?.status !== 404) {
        toast.error('Failed to load business settings');
      } else {
        console.log('ℹ️ No business settings configured yet (404)');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateSettings = useCallback(async (data: AppConfig): Promise<boolean> => {
    const token = requireToken();
    if (!token) return false;

    try {
      const response = await settingsService.saveSettings(data);

      if (response.success && response.data) {
        setSettings(response.data);
        toast.success('Business settings updated successfully!');
        return true;
      }

      return false;
    } catch (error: any) {
      console.error('Error updating business settings:', error);

      // Handle API error response format
      if (error.response?.data?.error?.error_description) {
        toast.error(error.response.data.error.error_description);
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to update business settings');
      }

      return false;
    }
  }, []);

  const updateSiteConfig = useCallback(async (data: Partial<SiteConfig>): Promise<boolean> => {
     const token = requireToken();
    if (!token || !settings) return false;
    if (!settings) {
      toast.error('Settings not loaded');
      return false;
    }

    const updatedSettings: AppConfig = {
      ...settings,
      site: {
        ...settings.site,
        ...data,
      },
    };

    return await updateSettings(updatedSettings);
  }, [settings, updateSettings]);

  // Initialize settings on mount (only if not on login flow)
  useEffect(() => {
    const token = getAuthToken();
    const isOnLoginFlow = window.location.pathname.includes('/dataloader') ||
                         window.location.pathname.includes('/handle/token');

    if (token && !isOnLoginFlow) {
      console.log("🔄 BusinessSettings: Token found on mount, fetching settings");
      fetchSettings();
    } else {
      setIsLoading(false);
      if (isOnLoginFlow) {
        console.log("ℹ️ BusinessSettings: On login flow, skipping auto-fetch (TokenHandler will handle it)");
      }
    }
  }, []); // Run only once on mount

  // Listen for token changes (from other tabs via storage event, or same window via custom event)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'auth_token' && e.newValue) {
        console.log('🔄 Token changed in other tab, fetching settings...');
        fetchSettings();
      }
    };

    const handleCustomTokenChange = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail?.token) {
        console.log('🔄 Token changed in same window via custom event');
        // Don't fetch here - TokenHandler will call fetchSettings directly
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('auth_token_changed', handleCustomTokenChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('auth_token_changed', handleCustomTokenChange);
    };
  }, [fetchSettings]);

  // Derived values for easy access
  const siteConfig = settings?.site || null;
  const expandedLogoUrl = siteConfig?.expanded_menu_logo_url || null;
  const collapsedLogoUrl = siteConfig?.collapsed_menu_logo_url || null;

  const contextValue: BusinessSettingsContextType = {
    settings,
    siteConfig,
    isLoading,
    isError,
    fetchSettings,
    updateSettings,
    updateSiteConfig,
    expandedLogoUrl,
    collapsedLogoUrl,
  };

  return (
    <BusinessSettingsContext.Provider value={contextValue}>
      {children}
    </BusinessSettingsContext.Provider>
  );
};

export default BusinessSettingsProvider;